<template>
  <div :class="['skill-modal', { active: visible }]" @click="handleBackdropClick">
    <div :class="['skill-modal-content', { 'no-select': !userStore.profile }]">
      <button class="skill-modal-close" @click="$emit('close')">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
      <div class="skill-modal-header">
        <div :class="['skill-modal-logo', iconType]">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <component :is="iconComponent" />
          </svg>
        </div>
        <div class="skill-modal-title-group">
          <h2 class="skill-modal-title">
            {{ title }}
          </h2>
          <div class="skill-modal-version">版本 {{ version }}</div>
        </div>
      </div>
      <p class="skill-modal-desc">
        {{ description }}
      </p>
      <div class="skill-modal-tags">
        <span v-for="tag in features" :key="tag" class="skill-modal-tag">
          {{ tag }}
        </span>
      </div>

      <div class="skill-modal-prompt">
        <div class="skill-modal-prompt-label">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          安装提示词
        </div>
        <div class="skill-modal-prompt-text">
          {{ installScript || '暂无安装提示词，请联系管理员配置' }}
        </div>
      </div>

      <div class="skill-modal-actions">
        <button
          :class="['btn', isCopied ? 'btn-copied' : 'btn-primary']"
          :disabled="isCopied"
          @click="copyInstallPrompt"
        >
          {{ isCopied ? '已复制！发送给你的龙虾即可' : '一键复制安装提示词' }}
        </button>
        <a v-if="!userStore.profile" class="btn btn-outline" :href="loginUrl"> 获取 APIKey </a>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, h, ref, watch, onMounted, onUnmounted, inject } from 'vue'
  import { ElMessage } from 'element-plus'
  import { useUserStore } from '@/store/modules/user'
  import { copyToClipboard } from '@/utils/clipboard'

  const isCopied = ref(false)
  let copyTimer = null

  const props = defineProps({
    visible: Boolean,
    title: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    features: {
      type: Array,
      default: () => []
    },
    iconType: {
      type: String,
      default: 'coming'
    },
    version: {
      type: String,
      default: 'v1.0.0'
    },
    installScript: {
      type: String,
      default: ''
    }
  })

  const emit = defineEmits(['close'])

  const userStore = useUserStore()
  const loginUrl = inject('loginUrl')

  // Lock body scroll when modal is open
  watch(
    () => props.visible,
    (newVal) => {
      if (newVal) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
  )

  const handleCopy = (e) => {
    if (!userStore.profile) {
      e.preventDefault()
      window.location.href = loginUrl
    }
  }

  onMounted(() => {
    document.addEventListener('copy', handleCopy)
  })

  onUnmounted(() => {
    document.removeEventListener('copy', handleCopy)
    if (copyTimer) {
      clearTimeout(copyTimer)
    }
  })

  const iconComponent = computed(() => {
    const icons = {
      hot: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.5 17.656 5.5a8 8 0 002.686-6.67'
      }),
      social: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
      }),
      report: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
      }),
      industry: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M3 3v18h18M7 14l4-4 4 4 5-6'
      }),
      macro: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-9a13 13 0 010 18m0-18a13 13 0 000 18m-9-9h18'
      }),
      enterprise: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
      }),
      bond: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
      }),
      coming: h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M12 6v6m0 0v6m0-6h6m-6 0H6'
      })
    }
    return icons[props.iconType] || icons.coming
  })

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      emit('close')
    }
  }

  const copyInstallPrompt = async () => {
    if (isCopied.value) return
    if (!userStore.profile) {
      window.location.href = loginUrl
      return
    }
    const apikey = userStore.apikey
    const promptText = apikey
      ? `${props.installScript}\nAPI Key: ${apikey}`
      : props.installScript || '暂无安装提示词，请联系管理员配置'
    const success = await copyToClipboard(promptText)
    if (success) {
      isCopied.value = true
      copyTimer = setTimeout(() => {
        isCopied.value = false
      }, 30000)
    } else {
      ElMessage.error('复制失败，请手动复制')
    }
  }
</script>

<style scoped>
  .skill-modal {
    display: none;
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    align-items: center;
    justify-content: center;
  }

  .skill-modal.active {
    display: flex;
  }

  .skill-modal-content {
    background: var(--bg-secondary);
    border-radius: 20px;
    padding: 32px;
    max-width: 600px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    position: relative;
    animation: modalSlideIn 0.3s ease;
  }

  .skill-modal-content.no-select {
    user-select: none;
    -webkit-user-select: none;
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .skill-modal-close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.05);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
  }

  .skill-modal-close:hover {
    background: rgba(0, 0, 0, 0.1);
  }

  [data-theme='dark'] .skill-modal-close {
    background: rgba(255, 255, 255, 0.1);
  }

  [data-theme='dark'] .skill-modal-close:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  .skill-modal-close svg {
    width: 20px;
    height: 20px;
    color: var(--text-secondary);
  }

  .skill-modal-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
  }

  .skill-modal-logo {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .skill-modal-logo svg {
    width: 32px;
    height: 32px;
  }

  .skill-modal-logo.hot {
    background: linear-gradient(135deg, #fee2e2, #fecaca);
  }

  .skill-modal-logo.hot svg {
    color: #ef4444;
  }

  .skill-modal-logo.social {
    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  }

  .skill-modal-logo.social svg {
    color: #3b82f6;
  }

  .skill-modal-logo.report {
    background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  }

  .skill-modal-logo.report svg {
    color: #10b981;
  }

  .skill-modal-logo.coming {
    background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
  }

  .skill-modal-logo.coming svg {
    color: #6b7280;
  }

  .skill-modal-logo.industry {
    background: linear-gradient(135deg, #ffedd5, #fed7aa);
  }

  .skill-modal-logo.industry svg {
    color: #ea580c;
  }

  .skill-modal-logo.macro {
    background: linear-gradient(135deg, #e0f2fe, #bae6fd);
  }

  .skill-modal-logo.macro svg {
    color: #0ea5e9;
  }

  .skill-modal-logo.enterprise {
    background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  }

  .skill-modal-logo.enterprise svg {
    color: #8b5cf6;
  }

  .skill-modal-logo.bond {
    background: linear-gradient(135deg, #fef9c3, #fde047);
  }

  .skill-modal-logo.bond svg {
    color: #ca8a04;
  }

  .skill-modal-title-group {
    flex: 1;
  }

  .skill-modal-title {
    font-size: 22px;
    font-weight: 800;
    color: var(--text-primary);
    margin-bottom: 4px;
  }

  .skill-modal-version {
    font-size: 12px;
    color: var(--text-secondary);
  }

  .skill-modal-desc {
    color: var(--text-secondary);
    font-size: 15px;
    line-height: 1.6;
    margin-bottom: 20px;
  }

  .skill-modal-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 24px;
  }

  .skill-modal-tag {
    padding: 6px 12px;
    background: rgba(99, 102, 241, 0.1);
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary);
  }

  [data-theme='dark'] .skill-modal-tag {
    background: rgba(99, 102, 241, 0.15);
  }

  .skill-modal-prompt {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;
  }

  [data-theme='dark'] .skill-modal-prompt {
    background: rgba(255, 255, 255, 0.05);
  }

  .skill-modal-prompt-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .skill-modal-prompt-label svg {
    width: 16px;
    height: 16px;
  }

  .skill-modal-prompt-text {
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    color: var(--text-primary);
    line-height: 1.6;
    background: rgba(0, 0, 0, 0.05);
    padding: 12px;
    border-radius: 8px;
    word-break: break-all;
  }

  [data-theme='dark'] .skill-modal-prompt-text {
    background: rgba(0, 0, 0, 0.2);
  }

  .skill-modal-actions {
    display: flex;
    gap: 12px;
    margin-top: 24px;
  }

  .skill-modal-actions .btn {
    flex: 1;
    justify-content: center;
  }

  .btn-primary {
    background: linear-gradient(135deg, #4f46e5, #4338ca);
    color: white;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
    box-shadow: 0 4px 15px rgba(79, 70, 229, 0.3);
  }

  .btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(79, 70, 229, 0.4);
  }

  .btn-copied {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 12px;
    cursor: not-allowed;
    transition: all 0.3s ease;
    border: none;
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  }

  .btn-outline {
    padding: 12px 24px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid var(--border-color);
    background: var(--card-bg);
    color: var(--text-primary);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .btn-outline:hover {
    border-color: var(--primary);
    color: var(--primary);
  }

  @media (max-width: 768px) {
    .skill-modal-content {
      padding: 24px;
      width: 95%;
    }

    .skill-modal-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    .skill-modal-actions {
      flex-direction: column;
    }

    .skill-modal-actions .btn {
      width: 100%;
    }
  }
</style>
